/**
 * Everything on a comanda card that depends on the clock.
 *
 * One ticker per card drives every timer on it. The card also stops ticking —
 * and stops pulsing — the moment the kitchen has finished every called wave:
 * a card that keeps pulsing after the work is done teaches the cook that the
 * pulsing means nothing.
 */
import { useEffect, useMemo, useState } from "react";
import { activeWaveOf, waveFiredAtMap } from "../lib/order-card";

export interface OrderTimers {
  /** Ticking clock, one per card. */
  now: number;
  /** When each wave was called into the kitchen. */
  waveFiredAt: Map<number, Date | null>;
  /** The wave cooking right now, or null when the kitchen is done. */
  activeWave: number | null;
  activeWaveFiredAt: Date | null;
  /** Seconds since the active wave was called. */
  elapsed: number;
  isOverdue: boolean;
  isWarning: boolean;
  /** Whether the card should pulse. */
  isTimerRunning: boolean;
}

/** One tick a second is what a kitchen timer needs; nothing here is smoother. */
const TICK_MS = 1_000;

export function useOrderTimers(order: any, items: any[]): OrderTimers {
  const [now, setNow] = useState(() => Date.now());

  const waveFiredAt = useMemo(() => waveFiredAtMap(items), [items]);
  const activeWave = useMemo(
    () => activeWaveOf(waveFiredAt, items),
    [waveFiredAt, items],
  );

  const activeWaveFiredAt = activeWave != null ? (waveFiredAt.get(activeWave) ?? null) : null;
  const isTimerRunning = activeWaveFiredAt !== null && activeWave !== null;

  // A card with nothing cooking has no clock to show, so it must not keep one
  // running: every tick re-renders the card, and a board of thirty finished
  // comande was re-rendering thirty cards a second for a number that never
  // changed. That alone kept a Raspberry's browser busy all afternoon.
  useEffect(() => {
    if (!isTimerRunning) return;
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(interval);
  }, [isTimerRunning]);

  const elapsed = activeWaveFiredAt
    ? Math.floor((now - activeWaveFiredAt.getTime()) / 1000)
    : 0;

  const elapsedMins = Math.floor(elapsed / 60);
  const estimate = order?.estimatedPrepMinutes;

  return {
    now,
    waveFiredAt,
    activeWave,
    activeWaveFiredAt,
    elapsed,
    isOverdue: Boolean(estimate && elapsedMins > estimate),
    isWarning: Boolean(estimate && elapsedMins > estimate * 0.75),
    isTimerRunning,
  };
}
